//Frontend permission resolution — the UI mirror of the server's
//requirePermission (node/permissions/catalog.js + resolver.js). Backed by the
//per-user PermList that /getuserdata and /login return (resolved live from the
//user's roles + overrides). Default-deny: no PermList means no permissions.

import { apiFetch } from "./api";

//Returns the Set of permission strings the user holds.
export function resolvePermissions(userData) {
  if (!userData || !Array.isArray(userData.PermList)) return new Set();
  return new Set(userData.PermList);
}

//Does userData hold ANY of the given permissions? Mirrors requirePermission's
//"passes if the user holds ANY of the given catalog permissions".
export function userCan(userData, ...perms) {
  const held = resolvePermissions(userData);
  return perms.some((perm) => held.has(perm));
}

//--- Role/override editing helpers (shared by the user modal + role editor) ---

//GET /permissioncatalog once per session: { permissions:[{name,group,description}],
//templates:[{RoleName,GrantsAll,IsLocked,Permissions}] }. The server is the
//single source of truth for the catalog — the UI never hardcodes it.
let _catalogPromise = null;
export function fetchPermissionCatalog() {
  if (!_catalogPromise) {
    _catalogPromise = apiFetch('/permissioncatalog', { method: 'GET' })
      .then((res) => res.json())
      .then((json) => {
        if (!json.ok) throw new Error(json.message || 'Failed to load permission catalog');
        return { permissions: json.permissions, templates: json.templates };
      })
      .catch((err) => { _catalogPromise = null; throw err; });
  }
  return _catalogPromise;
}

//The role-derived permission Set for a set of selected role names, given the
//tenant's roles from /getroles ({RoleName, GrantsAll, Permissions}). A GrantsAll
//role contributes the whole catalog; others contribute their Permissions. This
//mirrors resolver.js getRoleBase on the client so the modal can preview the
//base as roles are toggled, before anything is saved.
export function computeRoleBase(rawRoles, selectedNames, allPermNames) {
  const base = new Set();
  for (const r of rawRoles) {
    if (!selectedNames.has(r.RoleName)) continue;
    if (r.GrantsAll) return new Set(allPermNames);
    for (const p of (r.Permissions || [])) base.add(p);
  }
  return base;
}

//Effective Set = role base with per-user overrides applied (Map name->bool,
//true=grant, false=deny). Mirrors resolver.js applyOverrides.
export function effectiveWithOverrides(base, overridesMap) {
  const out = new Set(base);
  for (const [name, grant] of overridesMap) {
    if (grant) out.add(name); else out.delete(name);
  }
  return out;
}
